<template>
  <div class="content-list-header">
    <div class="content-list-header__left">
      <slot name="count-text">
        <span class="content-list-header__count">共{{ total }}条</span>
      </slot>
      <slot name="after-count" />
    </div>
    <slot name="right">
      <div class="content-list-header__help-wrapper">
        <ConsultantPopup position="bottom">
          <template #trigger>
            <div class="content-list-header__help-link">
              <span class="content-list-header__help-text">有疑问，</span>
              <span class="content-list-header__help-link-text">立即咨询顾问</span>
              <SvgIcon
                name="chat-help"
                :size="helpIconSize"
                class="content-list-header__help-icon"
              />
            </div>
          </template>
        </ConsultantPopup>
      </div>
    </slot>
  </div>
</template>

<script setup>
/**
 * @description 内容列表头部组件，支持总数显示和帮助链接
 * @param {number} total - 总记录数
 * @slot after-count - 在计数文本下方插入的内容（如移动端筛选按钮）
 * @slot right - 替换右侧默认咨询入口（如公告中心排序）
 * @example
 * <!-- 基础用法 -->
 * <ContentListHeader :total="100" />
 *
 * <!-- 带移动端筛选按钮 -->
 * <ContentListHeader :total="100">
 *   <template #after-count>
 *     <div class="mobile-filter-btn" @click="showFilter = true">
 *       筛选 <icon-apps />
 *     </div>
 *   </template>
 * </ContentListHeader>
 */

// 1. Imports
import ConsultantPopup from '~/components/base/consultant/ConsultantWrapper.vue'
import { useMediaQuery } from '~/composables/useMediaQuery'
import { computed } from 'vue'

const { isMobile } = useMediaQuery()
const helpIconSize = computed(() => (isMobile.value ? 12 : 16))

// 2. Props
const props = defineProps({
  /**
   * @description 总记录数
   * @type {number}
   */
  total: {
    type: Number,
    required: true,
  },
})
</script>

<style lang="scss" scoped>
.content-list-header {
  display: flex;
  min-height: 22px;
  justify-content: space-between;
  align-items: center;
  line-height: 22px;
  font-size: 14px;

  &__left {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  &__count {
    font-size: 14px;
    color: $text-subtitle-color;

    @include respond-to-max('lg') {
      font-size: 13px;
      color: $text-subtitle-color;
    }
  }

  &__help-wrapper {
    display: flex;
    align-items: center;
    gap: 3px;
    height: 22px;
    line-height: 22px;
  }

  &__help-text {
    display: inline-flex;
    align-items: center;
    height: 22px;
    font-size: 14px;
    line-height: 22px;
    color: $text-content-color;
    transition: color 0.3s;

    @include respond-to-max('lg') {
      font-size: 13px;
    }
  }

  &__help-link {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    height: 22px;
    font-size: 14px;
    line-height: 22px;
    color: $primary-color;
    text-decoration: none;
    cursor: pointer;
    transition: opacity 0.3s;

    &:hover {
      opacity: 0.8;
    }

    @include respond-to-max('lg') {
      font-size: 13px;
      gap: 4px;
    }
  }

  &__help-link-text {
    display: inline-flex;
    align-items: center;
    height: 22px;
    line-height: 22px;
  }

  &__help-icon {
    display: block;
    flex-shrink: 0;
    color: $primary-color;

    @include respond-to-max('lg') {
      width: 12px !important;
      height: 12px !important;

      :deep(svg) {
        width: 12px;
        height: 12px;
      }
    }
  }
}
</style>
